import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [registrations, setRegistrations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Refs to store initial values (from page load) for percentage calculation
  const initialTotalRegistrations = useRef(null);
  const initialPaidAttendees = useRef(null);
  const initialTicketsGenerated = useRef(null);
  const initialCheckedIn = useRef(null);
  
  // Refs to store previous values for real-time percentage calculation
  const prevTotalRegistrations = useRef(null);
  const prevPaidAttendees = useRef(null);
  const prevTicketsGenerated = useRef(null);
  const prevCheckedIn = useRef(null);
  
  // State to store percentage changes
  const [totalRegChange, setTotalRegChange] = useState(null);
  const [paidAttendeesChange, setPaidAttendeesChange] = useState(null);
  const [ticketsChange, setTicketsChange] = useState(null);
  const [checkedInChange, setCheckedInChange] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        fetchRegistrations();
        fetchTickets();
      } else {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Automatically open export modal when coming from other pages with ?export=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('export') === '1') {
      setShowExportModal(true);
    }
  }, [location.search]);

  const fetchRegistrations = () => {
    try {
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, orderBy('registeredAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const registrationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRegistrations(registrationsData);
        setLoading(false);
      }, (error) => {
        console.error('Error fetching registrations:', error);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up registrations listener:', error);
      setLoading(false);
    }
  };

  const fetchTickets = () => {
    try {
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, orderBy('generatedAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const ticketsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTickets(ticketsData);
      }, (error) => {
        console.error('Error fetching tickets:', error);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up tickets listener:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminUser');
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Calculate statistics with useMemo for real-time updates
  const paidStatuses = useMemo(() => ['paid', 'confirmed', 'ticket-sent', 'checked-in'], []);
  
  const totalRegistrations = useMemo(() => {
    return registrations.filter(r => r.status !== 'deleted').length;
  }, [registrations]);

  const paidAttendees = useMemo(() => {
    return registrations.filter(r => paidStatuses.includes(r.status) && r.status !== 'deleted').length;
  }, [registrations, paidStatuses]);

  const ticketsGenerated = useMemo(() => {
    const activeTickets = tickets.filter(t => t.status === 'active' || t.status === undefined);
    const uniqueUserEmails = new Set(activeTickets.map(t => t.userEmail).filter(Boolean));
    return uniqueUserEmails.size;
  }, [tickets]);

  const checkedIn = useMemo(() => {
    return registrations.filter(r => r.status === 'checked-in' && r.status !== 'deleted').length;
  }, [registrations]);

  // Get recent registrations (last 5)
  const recentRegistrations = useMemo(() => {
    return registrations
      .filter(r => r.status !== 'deleted')
      .slice(0, 5);
  }, [registrations]);

  // Initialize values when data first loads
  useEffect(() => {
    if (!loading) {
      // Set initial values on first load
      if (initialTotalRegistrations.current === null) {
        initialTotalRegistrations.current = totalRegistrations;
        initialPaidAttendees.current = paidAttendees;
        initialTicketsGenerated.current = ticketsGenerated;
        initialCheckedIn.current = checkedIn;
      }
      
      // Initialize previous values
      if (prevTotalRegistrations.current === null) {
        prevTotalRegistrations.current = totalRegistrations;
        prevPaidAttendees.current = paidAttendees;
        prevTicketsGenerated.current = ticketsGenerated;
        prevCheckedIn.current = checkedIn;
      }
    }
  }, [loading, totalRegistrations, paidAttendees, ticketsGenerated, checkedIn]);

  // Calculate percentage changes (real-time updates)
  useEffect(() => {
    // Only calculate if we have data loaded (not in loading state)
    if (loading) return;

    // Calculate percentage change for Total Registrations
    if (prevTotalRegistrations.current !== null && totalRegistrations !== prevTotalRegistrations.current) {
      if (prevTotalRegistrations.current > 0) {
        const change = ((totalRegistrations - prevTotalRegistrations.current) / prevTotalRegistrations.current) * 100;
        setTotalRegChange(change);
      } else if (totalRegistrations > 0) {
        // If previous was 0 and current is > 0, show 100% increase
        setTotalRegChange(100);
      }
    }
    prevTotalRegistrations.current = totalRegistrations;

    // Calculate percentage change for Paid Attendees
    if (prevPaidAttendees.current !== null && paidAttendees !== prevPaidAttendees.current) {
      if (prevPaidAttendees.current > 0) {
        const change = ((paidAttendees - prevPaidAttendees.current) / prevPaidAttendees.current) * 100;
        setPaidAttendeesChange(change);
      } else if (paidAttendees > 0) {
        setPaidAttendeesChange(100);
      }
    }
    prevPaidAttendees.current = paidAttendees;

    // Calculate percentage change for Tickets Generated
    if (prevTicketsGenerated.current !== null && ticketsGenerated !== prevTicketsGenerated.current) {
      if (prevTicketsGenerated.current > 0) {
        const change = ((ticketsGenerated - prevTicketsGenerated.current) / prevTicketsGenerated.current) * 100;
        setTicketsChange(change);
      } else if (ticketsGenerated > 0) {
        setTicketsChange(100);
      }
    }
    prevTicketsGenerated.current = ticketsGenerated;

    // Calculate percentage change for Checked In
    if (prevCheckedIn.current !== null && checkedIn !== prevCheckedIn.current) {
      if (prevCheckedIn.current > 0) {
        const change = ((checkedIn - prevCheckedIn.current) / prevCheckedIn.current) * 100;
        setCheckedInChange(change);
      } else if (checkedIn > 0) {
        setCheckedInChange(100);
      }
    }
    prevCheckedIn.current = checkedIn;
  }, [totalRegistrations, paidAttendees, ticketsGenerated, checkedIn, loading]);

  // Helper function to format percentage change
  const formatPercentageChange = (change) => {
    if (change === null || change === undefined) return null;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  // Helper function to get time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`;
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    const statusMap = {
      'paid': { text: 'Paid', bg: '#F0FDF4', color: '#15803D' },
      'confirmed': { text: 'Paid', bg: '#F0FDF4', color: '#15803D' },
      'pending': { text: 'Pending', bg: '#FEF9C3', color: '#A16207' },
      'checked-in': { text: 'Checked In', bg: '#F0FDF4', color: '#15803D' },
      'ticket-sent': { text: 'Ticket Sent', bg: '#EFF6FF', color: '#1D4ED8' }
    };
    return statusMap[status] || { text: status, bg: '#F3F4F6', color: '#374151' };
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Helper function to format date for CSV
  const formatDateForCSV = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Export function to convert data to CSV
  const exportToCSV = (data, headers, filename) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportModal(false);
  };

  // Export all registrations
  const handleExportRegistrations = () => {
    const headers = ['Name', 'Email', 'Phone Number', 'Gender', 'Status', 'Amount', 'Pricing Type', 'Registered At', 'Paid At', 'Checked In At'];
    const rows = registrations
      .filter(reg => reg.status !== 'deleted')
      .map(reg => [
        reg.fullName || '',
        reg.email || '',
        reg.phoneNumber || '',
        reg.gender || '',
        reg.status || '',
        reg.amount || '0',
        reg.pricingType || '',
        formatDateForCSV(reg.registeredAt),
        formatDateForCSV(reg.paidAt),
        formatDateForCSV(reg.checkedInAt)
      ]);
    exportToCSV(rows, headers, `all_registrations_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export paid registrations
  const handleExportPaidRegistrations = () => {
    const paidStatuses = ['paid', 'confirmed', 'ticket-sent', 'checked-in'];
    const headers = ['Name', 'Email', 'Phone Number', 'Gender', 'Status', 'Amount', 'Pricing Type', 'Registered At', 'Paid At', 'Checked In At'];
    const rows = registrations
      .filter(reg => paidStatuses.includes(reg.status) && reg.status !== 'deleted')
      .map(reg => [
        reg.fullName || '',
        reg.email || '',
        reg.phoneNumber || '',
        reg.gender || '',
        reg.status || '',
        reg.amount || '0',
        reg.pricingType || '',
        formatDateForCSV(reg.registeredAt),
        formatDateForCSV(reg.paidAt),
        formatDateForCSV(reg.checkedInAt)
      ]);
    exportToCSV(rows, headers, `paid_registrations_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export tickets
  const handleExportTickets = () => {
    const headers = ['Ticket ID', 'User Name', 'User Email', 'Status', 'Generated At', 'Registration ID'];
    const rows = tickets.map(ticket => [
      ticket.ticketId || '',
      ticket.userName || '',
      ticket.userEmail || '',
      ticket.status || 'active',
      formatDateForCSV(ticket.generatedAt),
      ticket.registrationId || ''
    ]);
    exportToCSV(rows, headers, `tickets_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export check-ins
  const handleExportCheckIns = () => {
    const headers = ['Name', 'Email', 'Phone Number', 'Gender', 'Ticket ID', 'Checked In At', 'Amount'];
    const rows = registrations
      .filter(reg => reg.status === 'checked-in' && reg.status !== 'deleted')
      .map(reg => [
        reg.fullName || '',
        reg.email || '',
        reg.phoneNumber || '',
        reg.gender || '',
        reg.ticketId || '',
        formatDateForCSV(reg.checkedInAt),
        reg.amount || '0'
      ]);
    exportToCSV(rows, headers, `check_ins_${new Date().toISOString().split('T')[0]}.csv`);
  };

    return (
    <div className="dashboard-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Close Button for Mobile */}
        <button className="sidebar-close-button" onClick={closeSidebar} aria-label="Close menu">
          <CloseIcon />
        </button>

        {/* Logo Section */}
        <div className="sidebar-logo">
          <div className="logo-icon">
            <img
              src={process.env.PUBLIC_URL + '/logo.jpg'}
              alt="Mountain Top University"
              className="admin-dashboard-logo-image"
            />
          </div>
          <div className="logo-text">
            <div className="logo-title">MTU Alumni</div>
            <div className="logo-subtitle">Event Management</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-item active" onClick={closeSidebar}>
            <DashboardIcon />
            <span>Dashboard</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); navigate('/admin/registrations'); }}>
            <RegistrationsIcon />
            <span>Registrations</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); navigate('/admin/generate-ticket'); }}>
            <TicketIcon />
            <span>Generate Ticket</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); navigate('/admin/checkin'); }}>
            <CheckInIcon />
            <span>Check-in</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); navigate('/admin/broadcasts'); }}>
            <BroadcastIcon />
            <span>Email Broadcast</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); setShowExportModal(true); }}>
            <ExportIcon />
            <span>Export Data</span>
          </div>
          <div className="nav-item" onClick={() => { closeSidebar(); navigate('/admin/settings'); }}>
            <SettingsIcon />
            <span>Settings</span>
          </div>

        </nav>

        {/* User Profile Section */}
        <div className="sidebar-user">
          <div className="user-info">
            <div className="user-avatar">
              {getUserInitials(user?.email || 'Admin User')}
            </div>
            <div className="user-details">
              <div className="user-name">Admin User</div>
              <div className="user-email">{user?.email || 'admin@mtu.edu'}</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="header-title">Dashboard Overview</h1>
            <p className="header-subtitle">MTU Alumni Association – Old Skool Hangout</p>
      </div>
          <div className="header-right">

            <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
              <HamburgerIcon />
                </button>
          </div>
        </header>

        {/* Main Content */}
        <div className="dashboard-content">
          {loading ? (
            <div className="skeleton-wrapper">
              {/* Statistics Cards Skeleton */}
              <div className="stats-grid">
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
              </div>

              {/* Recent Registrations Skeleton */}
              <div className="content-grid">
                <SkeletonTableCard />
              </div>

              {/* Chart Card Skeleton */}
              <SkeletonChartCard />
            </div>
          ) : (
            <>
              {/* Statistics Cards */}
              <div className="stats-grid">
                <StatCard
                  icon={<UsersIcon />}
                  title="Total Registrations"
                  value={totalRegistrations}
                  change={formatPercentageChange(totalRegChange)}
                  iconBg="#EFF6FF"
                  iconColor="#003399"
                />
                <StatCard
                  icon={<CheckCircleIcon />}
                  title="Paid Attendees"
                  value={paidAttendees}
                  change={formatPercentageChange(paidAttendeesChange)}
                  iconBg="#F0FDF4"
                  iconColor="#16A34A"
                />
                <StatCard
                  icon={<TicketIcon />}
                  title="Tickets Generated"
                  value={ticketsGenerated}
                  change={formatPercentageChange(ticketsChange)}
                  badge="Active"
                  iconBg="#FAF5FF"
                  iconColor="#9333EA"
                />
                <StatCard
                  icon={<CheckInIcon />}
                  title="Checked In"
                  value={checkedIn}
                  change={formatPercentageChange(checkedInChange)}
                  badge="Live"
                  iconBg="#FEF9C3"
                  iconColor="#EAB308"
                />
              </div>

              {/* Recent Registrations */}
              <div className="content-grid">
                <div className="registrations-card">
                  <div className="card-header">
                    <h3 className="card-title">Recent Registrations</h3>
                    <button className="view-all-button" onClick={() => navigate('/admin/registrations')}>View All</button>
                  </div>
                  <div className="table-container">
                    <table className="registrations-table">
                    <thead>
                        <tr>
                          <th>NAME</th>
                          <th>EMAIL</th>
                          <th>PHONE</th>
                          <th>GENDER</th>
                          <th>STATUS</th>
                          <th>TIME</th>
                      </tr>
                    </thead>
                    <tbody>
                        {recentRegistrations.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="empty-state">No registrations yet</td>
                          </tr>
                        ) : (
                          recentRegistrations.map((reg) => {
                            const statusBadge = getStatusBadge(reg.status);
                            return (
                              <tr key={reg.id}>
                                <td>
                                  <div className="user-cell">
                                    <div className="table-avatar">
                                      {getUserInitials(reg.fullName)}
                                    </div>
                                    <span>{reg.fullName}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className="email-cell">{reg.email}</span>
                          </td>
                                <td>
                                  <span className="phone-cell">{reg.phoneNumber || 'N/A'}</span>
                          </td>
                                <td>
                                  <span className="gender-cell">{reg.gender || 'N/A'}</span>
                          </td>
                                <td>
                                  <span className="status-badge" style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}>
                                    {statusBadge.text}
                            </span>
                          </td>
                                <td>{getTimeAgo(reg.registeredAt)}</td>
                        </tr>
                            );
                          })
                        )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            </>
              )}
            </div>
      </main>

      {/* Export Modal */}
      {showExportModal && (
        <div className="export-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <h2>Export Data</h2>
              <button className="export-modal-close" onClick={() => setShowExportModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="export-modal-body">
              <p className="export-modal-description">Select the data you want to export:</p>
              <div className="export-options">
                <button className="export-option-button" onClick={handleExportRegistrations}>
                  <RegistrationsIcon />
                  <div className="export-option-info">
                    <div className="export-option-title">All Registrations</div>
                    <div className="export-option-count">{registrations.filter(r => r.status !== 'deleted').length} records</div>
                  </div>
                </button>
                <button className="export-option-button" onClick={handleExportPaidRegistrations}>
                  <CheckCircleIcon />
                  <div className="export-option-info">
                    <div className="export-option-title">Paid Registrations</div>
                    <div className="export-option-count">{registrations.filter(r => ['paid', 'confirmed', 'ticket-sent', 'checked-in'].includes(r.status) && r.status !== 'deleted').length} records</div>
                  </div>
                </button>
                <button className="export-option-button" onClick={handleExportTickets}>
                  <TicketIcon />
                  <div className="export-option-info">
                    <div className="export-option-title">Tickets</div>
                    <div className="export-option-count">{tickets.length} records</div>
                  </div>
                </button>
                <button className="export-option-button" onClick={handleExportCheckIns}>
                  <CheckInIcon />
                  <div className="export-option-info">
                    <div className="export-option-title">Check-ins</div>
                    <div className="export-option-count">{registrations.filter(r => r.status === 'checked-in' && r.status !== 'deleted').length} records</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          </div>
  );
}

// Stat Card Component
function StatCard({ icon, title, value, change, badge, iconBg, iconColor }) {
  // Determine if change is positive or negative
  const isPositive = change && !change.startsWith('-');
  
  return (
    <div className="stat-card">
      <div className="stat-header">
        <div className="stat-icon" style={{ backgroundColor: iconBg }}>
          <div style={{ color: iconColor }}>{icon}</div>
        </div>
        {change && (
          <span className={`stat-change ${isPositive ? 'positive' : 'negative'}`}>{change}</span>
        )}
        {badge && (
          <span className="stat-badge">{badge}</span>
        )}
      </div>
      <p className="stat-label">{title}</p>
      <h3 className="stat-value">{value}</h3>
    </div>
  );
}

// Skeleton Components
function SkeletonStatCard() {
  return (
    <div className="stat-card skeleton-card">
      <div className="stat-header">
        <div className="skeleton skeleton-icon"></div>
        <div className="skeleton skeleton-badge"></div>
      </div>
      <div className="skeleton skeleton-label"></div>
      <div className="skeleton skeleton-value"></div>
    </div>
  );
}

function SkeletonTableCard() {
  return (
    <div className="registrations-card skeleton-card">
      <div className="card-header">
        <div className="skeleton skeleton-title"></div>
        <div className="skeleton skeleton-button"></div>
      </div>
      <div className="table-container">
        <table className="registrations-table">
          <thead>
            <tr>
              <th><div className="skeleton skeleton-th"></div></th>
              <th><div className="skeleton skeleton-th"></div></th>
              <th><div className="skeleton skeleton-th"></div></th>
              <th><div className="skeleton skeleton-th"></div></th>
              <th><div className="skeleton skeleton-th"></div></th>
              <th><div className="skeleton skeleton-th"></div></th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                <td>
                  <div className="user-cell">
                    <div className="skeleton skeleton-avatar"></div>
                    <div className="skeleton skeleton-name"></div>
                  </div>
                </td>
                <td><div className="skeleton skeleton-email"></div></td>
                <td><div className="skeleton skeleton-email"></div></td>
                <td><div className="skeleton skeleton-email"></div></td>
                <td><div className="skeleton skeleton-badge-small"></div></td>
                <td><div className="skeleton skeleton-time"></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SkeletonChartCard() {
  return (
    <div className="chart-card skeleton-card">
      <div className="skeleton skeleton-title" style={{ marginBottom: '24px' }}></div>
      <div className="skeleton skeleton-chart"></div>
    </div>
  );
}

// Icon Components
const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 1C0 0.45 0.45 0 1 0H6C6.55 0 7 0.45 7 1V6C7 6.55 6.55 7 6 7H1C0.45 7 0 6.55 0 6V1ZM9 1C9 0.45 9.45 0 10 0H15C15.55 0 16 0.45 16 1V6C16 6.55 15.55 7 15 7H10C9.45 7 9 6.55 9 6V1ZM0 10C0 9.45 0.45 9 1 9H6C6.55 9 7 9.45 7 10V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V10ZM9 10C9 9.45 9.45 9 10 9H15C15.55 9 16 9.45 16 10V15C16 15.55 15.55 16 15 16H10C9.45 16 9 15.55 9 15V10Z" fill="currentColor" />
  </svg>
);

const RegistrationsIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M0 0V16H20V0H0ZM2 2H18V14H2V2ZM4 4H16V6H4V4ZM4 8H16V10H4V8ZM4 12H12V14H4V12Z" fill="currentColor" />
  </svg>
);

const TicketIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2C0 1.45 0.45 1 1 1H17C17.55 1 18 1.45 18 2V4C17.45 4 17 4.45 17 5C17 5.55 17.45 6 18 6V8C18 8.55 17.55 9 17 9H1C0.45 9 0 8.55 0 8V6C0.55 6 1 5.55 1 5C1 4.45 0.55 4 0 4V2Z" fill="currentColor" />
  </svg>
);

const CheckInIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M0 1V15H14V1H0ZM2 3H12V13H2V3ZM6 5H8V7H6V5ZM6 9H8V11H6V9Z" fill="currentColor" />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 0V16H16V0H0ZM2 2H14V14H2V2ZM4 4H12V6H4V4ZM4 8H12V10H4V8ZM4 12H8V14H4V12Z" fill="currentColor" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2H6V0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H6V14ZM11.59 10.59L9.59 12.59L11.18 14.18L15.36 10L11.18 5.82L9.59 7.41L11.59 9.41H5V11.41H11.59Z" fill="currentColor" />
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2H18C19.1 2 20 2.9 20 4V12C20 13.1 19.1 14 18 14H2C0.9 14 0 13.1 0 12V4C0 2.9 0.9 2 2 2ZM18 4L10 8.5L2 4V4L10 0.5L18 4ZM2 6V12H18V6L10 10.5L2 6Z" fill="currentColor" />
  </svg>
);


const UsersIcon = () => (
  <svg width="25" height="20" viewBox="0 0 25 20" fill="none">
    <path d="M12.5 0C9.58 0 7.25 2.33 7.25 5.25C7.25 8.17 9.58 10.5 12.5 10.5C15.42 10.5 17.75 8.17 17.75 5.25C17.75 2.33 15.42 0 12.5 0ZM12.5 13.5C8.66 13.5 0 15.34 0 19.25V20H25V19.25C25 15.34 16.34 13.5 12.5 13.5Z" fill="currentColor" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor" />
  </svg>
);


const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 12.5C11.38 12.5 12.5 11.38 12.5 10C12.5 8.62 11.38 7.5 10 7.5C8.62 7.5 7.5 8.62 7.5 10C7.5 11.38 8.62 12.5 10 12.5ZM15.92 6.42L14.5 5C14.5 3.62 13.38 2.5 12 2.5L10.58 1.08C9.2 1.08 8.08 2.2 8.08 3.58L6.66 5C5.28 5 4.16 6.12 4.16 7.5L2.74 8.92C2.74 10.3 3.86 11.42 5.24 11.42L6.66 12.84C6.66 14.22 7.78 15.34 9.16 15.34L10.58 16.76C11.96 16.76 13.08 15.64 13.08 14.26L14.5 12.84C15.88 12.84 17 11.72 17 10.34L18.42 8.92C18.42 7.54 17.3 6.42 15.92 6.42Z" fill="currentColor" />
  </svg>
);

export default AdminDashboard;
